/**
 * <required-tips-component> - mandatory tail pipe selection for exhaust systems sold without tips.
 *
 * The server-rendered block (blocks/product-required-tips.liquid) already does the hard part with no
 * JS: the tip radios are associated to the product form and marked `required`, so the browser blocks
 * "Add to cart" until one is chosen, and the chosen tip name rides along as a "Tailpipes: ..."
 * property on the system line.
 *
 * This layer adds the chosen tail pipe set to the cart as its own paid line - it is a separate
 * product, separately priced and stocked, so it cannot be a mere line item property. We wait for the
 * SYSTEM's own add-to-cart to succeed (its CartAddEvent), then add the selected tip with a matching
 * quantity and a "Fitted to: <system>" property so the pairing is visible in the cart and on the
 * order, and re-announce the cart so the drawer shows both lines.
 *
 * Fail-safe by design: if this script never runs or errors, the system still adds with the recorded
 * tip choice - the purchase is never blocked, staff can see the intended tip on the order.
 */

import { CartAddEvent, CartUpdateEvent } from '@theme/events';

class RequiredTipsComponent extends HTMLElement {
  /** @type {string | null} */ #systemProductId = null;
  /** @type {string} */ #systemTitle = '';
  /** @type {boolean} */ #adding = false;
  /** @type {AbortController} */ #abort = new AbortController();

  connectedCallback() {
    this.#systemTitle = this.getAttribute('data-system-title') || '';

    // The system this selector belongs to: the product form in the same section.
    const section = this.closest('.shopify-section') || document;
    const productForm = section.querySelector('product-form-component');
    this.#systemProductId = productForm instanceof HTMLElement ? productForm.dataset.productId || null : null;

    // A tip add-to-cart is triggered by the system's own successful add (CartAddEvent).
    document.addEventListener(CartAddEvent.eventName, this.#onCartAdd, { signal: this.#abort.signal });
  }

  disconnectedCallback() {
    this.#abort.abort();
  }

  /** The variant id of the currently chosen tail pipe set, or null if none picked yet. */
  #selectedVariantId() {
    const checked = /** @type {HTMLInputElement | null} */ (this.querySelector('.required-tips__radio:checked'));
    return checked?.dataset.variantId || null;
  }

  /** @param {CartAddEvent} event */
  #onCartAdd = (event) => {
    const data = event.detail?.data;
    if (!data) return;

    // Only react to the SYSTEM being added: a genuine product-form add for this product, not our
    // own tip add, and not an errored add.
    if (data.source !== 'product-form-component') return;
    if (data.didError) return;
    if (this.#systemProductId && String(data.productId) !== String(this.#systemProductId)) return;

    const variantId = this.#selectedVariantId();
    if (!variantId || this.#adding) return;

    const quantity = Number(data.itemCount) > 0 ? Number(data.itemCount) : 1;
    this.#addTip(variantId, quantity);
  };

  /**
   * Adds the chosen tail pipe set as its own cart line, then re-announces the cart so the drawer and
   * cart page reflect both lines. Mirrors the successful-add event shape used by product-form.js.
   * @param {string} variantId
   * @param {number} quantity
   */
  async #addTip(variantId, quantity) {
    this.#adding = true;

    // Collect cart section ids so the response can refresh the drawer/cart items in place.
    const sectionIds = new Set();
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) sectionIds.add(el.dataset.sectionId);
    });

    const payload = {
      items: [
        {
          id: Number(variantId),
          quantity,
          properties: this.#systemTitle ? { 'Fitted to': this.#systemTitle } : {},
        },
      ],
      sections: Array.from(sectionIds).join(','),
      sections_url: window.location.pathname,
    };

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });
      const result = await response.json();
      if (result.status) return; // add failed (e.g. out of stock) - leave the system line as-is.

      let cart = null;
      try {
        cart = await (await fetch('/cart.js')).json();
      } catch {
        /* non-fatal: the event below still refreshes the items section */
      }

      const detail = {
        source: 'required-tips-component',
        itemCount: quantity,
        sections: result.sections,
      };
      this.dispatchEvent(new CartAddEvent(cart ?? undefined, this.id, detail));
      document.dispatchEvent(new CartUpdateEvent(cart ?? {}, this.id, detail));
    } catch (error) {
      console.error('required-tips: failed to add tail pipe set', error);
    } finally {
      this.#adding = false;
    }
  }
}

if (!customElements.get('required-tips-component')) {
  customElements.define('required-tips-component', RequiredTipsComponent);
}
